import type { RngState } from '../math/rng';
import { chance, weightedIndex } from '../math/rng';
import { droppableDefs } from '../data/parts/partDefs';
import { rollPartInstance } from '../data/parts/roll';
import { RARITIES, type PartInstance, type Rarity } from '../data/parts/partTypes';
import type { Inventory } from '../state';

/** Loot drops (THE_MIND_AND_PARTS.md §3). Parts are RARE; deterministic via RNG. */

export interface DropResult {
  instance: PartInstance | null;
  rarity?: Rarity;
}

/** Rarity weights ramp toward higher tiers with wave/chassis. Bosses bias up.
 *  Tuned so that at full ramp (t=1): Nerfed stops dropping entirely, Lit is the
 *  most common tier, and Based caps around 5%. Early game is unchanged (t=0 is
 *  still ~65% Nerfed). Bosses shift weight from Mid toward Sweaty, same shape. */
function rarityWeights(wave: number, tier: number, isBoss: boolean): number[] {
  const t = Math.min(1, (wave + (tier - 1) * 12) / 80);
  const boss = isBoss ? 1 : 0;
  return [
    Math.max(0, 5 * (1 - t) - boss * 2), // common    (Nerfed) -> 0 at full ramp
    2 + t * 1.0 - boss * 0.3, // uncommon  (Mid)
    0.6 + t * 3.2, // rare      (Lit) -> the full-ramp peak
    0.1 + t * 2.6 + boss * 0.3, // epic      (Sweaty)
    0.02 + t * 0.48, // legendary (Based) -> ~5% cap at full ramp
  ];
}

export function dropChance(wave: number, isBoss: boolean, dropRateMult: number): number {
  const base = isBoss ? 1.0 : 0.02 + Math.min(0.06, wave * 0.0005);
  return Math.min(1, base * Math.max(1, dropRateMult));
}

export function rollDrop(
  wave: number,
  tier: number,
  isBoss: boolean,
  inv: Inventory,
  rng: RngState,
  dropRateMult: number,
): DropResult {
  if (!chance(rng, dropChance(wave, isBoss, dropRateMult))) return { instance: null };
  if (inv.parts.length >= inv.cap) return { instance: null }; // Storage full — drop skipped

  const rarity = RARITIES[weightedIndex(rng, rarityWeights(wave, tier, isBoss))] ?? 'common';

  const pool = droppableDefs(tier);
  if (pool.ids.length === 0) return { instance: null };
  const defId = pool.ids[weightedIndex(rng, pool.weights)] ?? pool.ids[0]!;

  const { instance, nextId } = rollPartInstance(defId, rarity, rng, inv.nextId);
  inv.nextId = nextId;
  inv.parts.push(instance);
  return { instance, rarity: instance.rarity };
}
